/**
 * The session: who is signed in, which firm they are acting for, and the toast queue.
 *
 * The active org lives here rather than in a route parameter, for a reason that matters
 * for isolation: every API call carries it as a header, and a firm switch must
 * invalidate the entire query cache. Putting it in the URL would make a stale link
 * capable of pointing at a firm the user has since been removed from — and the cache
 * would happily serve the previous firm's rows until it expired.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { OrgRole } from "@onecpa/shared";
import type { ToastMessage } from "@onecpa/ui";
import { api, type MeResponse, type OrgSummary } from "../api/client.js";
import { useAuth } from "../auth/AuthProvider.js";

/**
 * Scoped per user.
 *
 * Two people sharing a machine otherwise inherit each other's last firm: if the
 * first switched to their second office and signed out, the next person who also
 * belongs to it lands there rather than on their own default.
 */
function activeOrgKey(uid: string): string {
  return `onecpa.activeOrgId:${uid}`;
}

export interface Session {
  me: MeResponse;
  orgId: string;
  orgName: string;
  /** The active firm in full, for anything that draws its identity. */
  org: OrgSummary;
  role: OrgRole;
  organizations: OrgSummary[];
  switchOrg(orgId: string): void;
  toast(text: string, tone?: ToastMessage["tone"]): void;
  toasts: ToastMessage[];
  dismissToast(id: string): void;
}

const SessionContext = createContext<Session | null>(null);

export function useSession(): Session {
  const context = useContext(SessionContext);
  if (!context) throw new Error("useSession must be used inside SessionProvider");
  return context;
}

/** Convenience for the many screens that gate a panel on the caller's role. */
export function useRole(): {
  role: OrgRole;
  isOwner: boolean;
  isManager: boolean;
  isStaff: boolean;
} {
  const { role } = useSession();
  return {
    role,
    isOwner: role === "owner",
    // "at least manager", which is how most money screens are gated.
    isManager: role === "owner" || role === "manager",
    isStaff: role === "staff",
  };
}

export function SessionProvider({
  children,
  fallback,
}: {
  children: ReactNode;
  fallback: (state: { loading: boolean; error: Error | null; noMembership: boolean }) => ReactNode;
}) {
  const { user } = useAuth();
  const uid = user?.uid ?? "";
  const queryClient = useQueryClient();
  const [activeOrgId, setActiveOrgId] = useState<string | null>(() =>
    uid ? localStorage.getItem(activeOrgKey(uid)) : null,
  );
  const [toasts, setToasts] = useState<ToastMessage[]>([]);

  const {
    data: me,
    isLoading,
    error,
  } = useQuery({
    // Keyed by the signed-in user, not just by name. The cache is already cleared
    // when the user changes, so this is belt and braces — but `me` carries the
    // role every screen gates on, and it is the one entry where serving the
    // previous person's copy for even a moment shows one user another's data.
    queryKey: ["me", uid],
    queryFn: api.me,
    enabled: Boolean(user),
  });

  const organizations = me?.organizations ?? [];
  const active = organizations.find((org) => org.id === activeOrgId) ?? organizations[0] ?? null;

  // Remember the choice, so a reload does not silently move an accountant who serves
  // two firms back to the first one.
  useEffect(() => {
    if (active && uid) localStorage.setItem(activeOrgKey(uid), active.id);
  }, [active, uid]);

  const switchOrg = useCallback(
    (orgId: string) => {
      setActiveOrgId(orgId);
      if (uid) localStorage.setItem(activeOrgKey(uid), orgId);
      // Everything cached belongs to the previous firm. Clearing rather than
      // invalidating means no request can resolve against the old org's data.
      queryClient.clear();
    },
    [queryClient, uid],
  );

  const toast = useCallback((text: string, tone: ToastMessage["tone"] = "success") => {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    setToasts((current) => [...current, { id, text, tone }]);
    // Auto-dismiss: a toast is a confirmation, not a message anyone must acknowledge.
    setTimeout(() => setToasts((current) => current.filter((item) => item.id !== id)), 5_000);
  }, []);

  const dismissToast = useCallback((id: string) => {
    setToasts((current) => current.filter((item) => item.id !== id));
  }, []);

  const value = useMemo<Session | null>(() => {
    if (!me || !active) return null;
    return {
      me,
      orgId: active.id,
      orgName: active.name,
      org: active,
      role: active.role,
      organizations,
      switchOrg,
      toast,
      toasts,
      dismissToast,
    };
  }, [me, active, organizations, switchOrg, toast, toasts, dismissToast]);

  if (!value) {
    return (
      <>
        {fallback({
          loading: isLoading,
          error: (error as Error | null) ?? null,
          noMembership: Boolean(me) && organizations.length === 0,
        })}
      </>
    );
  }

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}
